import { AnimatePresence, motion } from "framer-motion";
import { toast } from "sonner";

export default function RoutePanel({ sites, routeIds, open, setOpen, onRemove, onClear, onSelect, onStartTour, onPoster }) {
  const chosen = routeIds.map((id) => sites.find((s) => s.id === id)).filter(Boolean);

  const copy = async () => {
    const url = `${window.location.origin}${window.location.pathname}#route=${routeIds.join(",")}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Route link copied — share the expedition");
    } catch {
      toast.error("Couldn't copy automatically — grab the URL bar instead");
    }
  };

  return (
    <>
      <button
        data-testid="route-pill"
        onClick={() => setOpen(!open)}
        className="flex items-center gap-2.5 rounded-full border border-[#D4AF37]/50 bg-[#12141C]/90 px-5 py-3 font-mono text-[10px] tracking-[0.25em] text-[#D4AF37] shadow-lg shadow-black/40 backdrop-blur transition-all duration-300 hover:bg-[#D4AF37] hover:text-[#0B0C10]"
      >
        ★ <span className="hidden sm:inline">EXPEDITION </span>ROUTE · {chosen.length}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="route-panel"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 24 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="fixed bottom-20 right-5 z-40 w-[calc(100vw-2.5rem)] max-w-[340px] border border-[#D4AF37]/30 bg-[#12141C]/95 p-5 backdrop-blur"
          >
            <p className="font-mono text-[9px] tracking-[0.35em] text-[#D4AF37]">
              YOUR EXPEDITION ROUTE
            </p>
            {chosen.length === 0 ? (
              <p className="mt-4 text-[13px] font-light leading-relaxed text-white/55">
                No sites yet. Tap the + on any index row — or inside a dossier —
                to plot your route.
              </p>
            ) : (
              <ul className="mt-4 max-h-64 space-y-1 overflow-y-auto">
                {chosen.map((s, i) => (
                  <li key={s.id}>
                    <div className="group flex items-center gap-3 py-2">
                      <button
                        data-testid={`route-open-${s.id}`}
                        onClick={() => {
                          onSelect(s);
                          setOpen(false);
                        }}
                        className="flex min-w-0 flex-1 items-baseline gap-3 text-left"
                      >
                        <span className="font-mono text-[9px] text-[#D4AF37]/60">
                          {String(i + 1).padStart(2, "0")}
                        </span>
                        <span className="truncate font-serif text-sm text-white/80 group-hover:text-[#D4AF37]">
                          {s.name}
                        </span>
                      </button>
                      <button
                        data-testid={`route-remove-${s.id}`}
                        onClick={() => onRemove(s.id)}
                        aria-label={`Remove ${s.name} from route`}
                        className="shrink-0 px-1 font-serif text-base text-white/40 transition-colors hover:text-[#FF9F1C]"
                      >
                        ×
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
            <button
              data-testid="route-start-tour"
              onClick={() => {
                setOpen(false);
                onStartTour();
              }}
              disabled={!chosen.length}
              className="mt-5 flex w-full items-center justify-center gap-3 border border-[#FF9F1C]/60 bg-[#FF9F1C]/10 py-3 font-mono text-[9px] tracking-[0.3em] text-[#FF9F1C] transition-all duration-300 hover:bg-[#FF9F1C] hover:text-[#0B0C10] disabled:cursor-not-allowed disabled:opacity-30"
            >
              ▶ START GUIDED TOUR
            </button>
            <button
              data-testid="route-poster"
              onClick={() => {
                setOpen(false);
                onPoster();
              }}
              disabled={!chosen.length}
              className="mt-2 w-full border border-white/20 py-2.5 font-mono text-[9px] tracking-[0.3em] text-white/60 transition-all duration-300 hover:border-[#D4AF37] hover:text-[#D4AF37] disabled:cursor-not-allowed disabled:opacity-30"
            >
              ◈ EXPEDITION POSTER
            </button>
            <div className="mt-2 flex gap-2">
              <button
                data-testid="route-copy"
                onClick={copy}
                disabled={!chosen.length}
                className="flex-1 bg-[#D4AF37] py-2.5 font-mono text-[9px] tracking-[0.25em] text-[#0B0C10] transition-colors hover:bg-[#FF9F1C] disabled:cursor-not-allowed disabled:opacity-30"
              >
                COPY SHARE LINK
              </button>
              <button
                data-testid="route-clear"
                onClick={onClear}
                disabled={!chosen.length}
                className="border border-white/20 px-4 py-2.5 font-mono text-[9px] tracking-[0.25em] text-white/55 transition-colors hover:border-[#FF9F1C] hover:text-[#FF9F1C] disabled:cursor-not-allowed disabled:opacity-30"
              >
                CLEAR
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
